import React, { useContext, useState } from "react"
import DeleteDialogueModal from "../DialogueModal"
import moment from "moment"
import SubscriptionTag from "../../components/subscription/SubscriptionTag"
import useDate from "../../hooks/useDate"
import useLoading from "../../hooks/useLoading"
import { Badge } from "../ui/badge"
import { Button } from "../ui/button"
import { SubscriptionContext } from "./SubscriptionContext"
import { SubscriptionRequests } from "../../api/app.service"
import { SvgChatEllipses, SvgLightning } from "../icons/"
import { useIntercom } from "react-use-intercom"
import { useNavigate } from "react-router-dom"
import { UserCtx } from "../../context/userContext"
import { useToast } from "../../hooks/useToast"

interface Props {
  isLatestPaymentDeclined?: boolean
  plans?: any
  isFreeForever: boolean
  isTrueLifeTimePromo: boolean
  foundingCoachPromo?: boolean
  hasValidFutureEndDate?: any
}

const CurrentSubscription: React.FC<Props> = ({
  isLatestPaymentDeclined,
  plans,
  isFreeForever,
  isTrueLifeTimePromo,
  foundingCoachPromo,
  hasValidFutureEndDate,
}) => {
  const { user } = useContext(UserCtx)
  const { subscription, getSubscription } = useContext(SubscriptionContext)
  const { toast } = useToast()
  const { startLoading, stopLoading } = useLoading()
  const { mdDate } = useDate()
  const { show } = useIntercom()

  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] =
    useState<boolean>(false)

  const navigate = useNavigate()

  const lifeTimeDuration =
    subscription?.promo_duration === 1000 && hasValidFutureEndDate(subscription)

  const notLifeTime =
    !subscription?.is_lifetime && subscription?.promo_duration !== 1000

  const lifeTimePromoEnding =
    subscription?.promo_duration === 1000 &&
    subscription?.subscription_end_date !== "None" &&
    new Date(subscription.subscription_end_date).getTime() <=
      new Date(new Date().setFullYear(new Date().getFullYear() + 3)).getTime()

  const paidLifeTimeSub =
    isTrueLifeTimePromo &&
    subscription?.promo_value !== 100 &&
    subscription?.promo_value_type === "percent" &&
    subscription?.subscription_end_date === "None"

  const navigateToSubPlans = () => {
    navigate("/coach/subscription/plans")
  }

  const navigateToPaymentCtr = () => {
    navigate("/account-settings/payment-center")
  }

  const handleCancelSubscriptionClick = () => {
    if (showCancelSubscriptionModal) {
    } else {
      setShowCancelSubscriptionModal(true)
    }
  }

  const getUserPlanFromPlans = (subscription: any, plans: any) => {
    if (!subscription || !plans) return null

    switch (subscription.recurring_interval) {
      case "annual":
        return plans.annual ?? null

      case "month":
        return plans.monthly ?? null

      default:
        return null
    }
  }

  const userPlan = getUserPlanFromPlans(subscription, plans)

  const cancelSubscription = () => {
    startLoading()
    SubscriptionRequests.cancelCoachSubscription({
      subscription_id: subscription?.subscription_id,
    })
      .then((data) => {
        getSubscription()
        toast.success(data.message)
        setShowCancelSubscriptionModal(false)
      })
      .catch((ex) => {
        console.error(ex)
        toast.error(ex.response.data.message)
        stopLoading()
      })
  }

  const getTrialDaysRemaining = () => {
    const expiration = moment(user?.trialExpiration)
    const now = moment()

    return Math.max(0, expiration.diff(now, "days"))
  }

  const noEndDate = Boolean(
    subscription?.subscription_end_date === "None" &&
      subscription?.subscription_next_payment_date &&
      new Date(subscription.subscription_next_payment_date).getFullYear() >=
        3000
  )

  const getCurrentPlanSection = () => {
    if (subscription && !noEndDate) {
      if (subscription?.subscription_end_date === "None" || paidLifeTimeSub) {
        return (
          <>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-4 w-full">
              <Button
                onClick={handleCancelSubscriptionClick}
                variant="ghost"
                className="h-12 px-6 py-3 rounded-[10px] border border-[#d1d9e0] flex items-center gap-2.5 w-full sm:w-auto"
              >
                <span className="text-black text-sm font-bold font-['Beausite Classic'] leading-normal">
                  Cancel Subscription
                </span>
              </Button>

              <Button
                onClick={navigateToPaymentCtr}
                variant="blurpleGradient"
                className="flex items-center gap-[10px] px-6 w-full sm:w-auto"
              >
                Update Payment Method
              </Button>
            </div>

            {showCancelSubscriptionModal && (
              <DeleteDialogueModal
                isWarningVariant
                setShowDialogueModal={setShowCancelSubscriptionModal}
                helpText={`Are you sure you want to cancel your subscription? If you cancel, your subscription will be active until ${mdDate(
                  subscription.subscription_next_payment_date
                )}`}
                header="Cancel Subscription?"
                confirmButtonText="Cancel Subscription"
                confirmCallbackFn={cancelSubscription}
              />
            )}
          </>
        )
      } else if (
        subscription?.subscription_end_date !== "None" &&
        subscription.subscription_promo_code !== "500FOREVER"
      ) {
        return
      }
    }
  }

  const calculateLifetimePrice = (
    subscription: any,
    userPlan: any
  ): number | null => {
    if (!subscription || !userPlan) return null
    if (!subscription.is_lifetime) return null

    const { promo_value, promo_value_type } = subscription
    const { amount } = userPlan

    if (!amount) return null

    // 100% free lifetime
    if (promo_value_type === "percent" && promo_value === 100) {
      return 0
    }

    // Percentage discount lifetime
    if (promo_value_type === "percent") {
      return Math.round(amount * (1 - promo_value / 100))
    }

    // Flat discount lifetime
    if (promo_value_type === "amount") {
      return Math.max(amount - promo_value, 0)
    }

    return amount
  }

  const lifetimePrice = calculateLifetimePrice(subscription, userPlan)

  return (
    <div className="main-shadow grow rounded-[16px] p-[24px] sm:min-w-[490px] md:h-fit md:p-[32px]">
      <div className="mb-[24px] flex w-full items-center justify-between">
        <h2 className="text-[22px] font-bold">Current Plan</h2>
      </div>

      <div className="border-grayMist flex w-full flex-col rounded-[24px] border p-[24px] md:p-[32px] bg-lightGray">
        {!subscription ? (
          <>
            <div className="mb-[96px] flex w-full justify-between font-bold">
              <div className={isLatestPaymentDeclined ? "invisible" : ""}>
                <SubscriptionTag subscriptionName="Free Trial" />
              </div>
              {(getTrialDaysRemaining() || 0) <= 0 &&
              !isLatestPaymentDeclined ? (
                <h4 className="text-vividRed text-[32px]">Expired</h4>
              ) : isLatestPaymentDeclined ? (
                <div className="text-right">
                  <h4 className="text-vividRed text-[32px] mb-1">Inactive</h4>
                  <p className="text-graySlate text-sm">
                    To regain access, please resubscribe
                  </p>
                </div>
              ) : (
                <h4 className="text-green text-[32px]">Free</h4>
              )}
            </div>
            <div className="flex flex-col-reverse sm:flex-row items-end sm:items-center justify-between w-full">
              <div className="flex flex-col gap-[8px] text-base text-right sm:text-left mt-4 sm:mt-0">
                <div className="flex items-center justify-end sm:justify-start gap-[8px]">
                  <SvgChatEllipses />
                  <p>
                    {isLatestPaymentDeclined
                      ? "Have questions?"
                      : "Need help deciding?"}
                  </p>
                </div>
                <button
                  onClick={() => show()}
                  className="text-blurple font-bold"
                >
                  Chat with our support
                </button>
              </div>

              <Button
                onClick={navigateToSubPlans}
                className="flex w-[158px] min-w-fit items-center gap-[10px] sm:ml-4"
              >
                {isLatestPaymentDeclined ? "Resubscribe" : "Upgrade Plan"}
                <SvgLightning />
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-[24px] flex items-center justify-between">
              {/* PLAN DETAILS */}
              <div className="flex flex-col gap-[12px]">
                <h2 className="text-[22px] font-bold">
                  {notLifeTime || lifeTimePromoEnding ? (
                    <>{`${
                      subscription?.recurring_interval === "annual"
                        ? "Annual Pro"
                        : subscription?.recurring_interval === "3-month"
                        ? "3 - Month BBHC Program"
                        : "Monthly Pro"
                    }`}</>
                  ) : (
                    <>Lifetime Subscription</>
                  )}
                </h2>
                {/* PRICE */}
                <div>
                  {subscription?.recurring_interval === "3-month" ? (
                    <Badge>one-time payment</Badge>
                  ) : (
                    foundingCoachPromo && (
                      <Badge variant="blurpleGradient">Founding Coach</Badge>
                    )
                  )}
                  <br />
                  <div>
                    {lifeTimeDuration || foundingCoachPromo ? (
                      <>
                        {isFreeForever ? (
                          <span className="text-blurple text-[42px] font-semibold font-['Beausite Classicr']">
                            Free
                          </span>
                        ) : (
                          <>
                            <span className="text-blurple text-[42px] font-semibold font-['Beausite Classic'] ml-2">
                              ${lifetimePrice}
                            </span>
                            <span className="text-black text-[28px] font-normal font-['Beausite Classic']">
                              /
                              {subscription?.recurring_interval === "month"
                                ? "month"
                                : "year"}
                            </span>
                          </>
                        )}

                        <div className="text-black text-[28px] font-normal font-['Beausite Classic']">
                          for the lifetime of your account!
                        </div>
                        <span className="text-graySlate text-base font-semibold font-['Beausite Classic']">
                          {subscription?.recurring_interval === "month"
                            ? "Originally $49/month"
                            : "Originally $490/year"}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-black text-[42px] font-semibold font-['Beausite Classic']">
                          {subscription?.recurring_interval !== "3-month" && (
                            <>$ {subscription?.amount}</>
                          )}
                        </span>
                        <span className="text-black text-[28px] font-normal font-['Beausite Classic']">
                          {subscription?.promotion_details?.lifetime && (
                            <>/one-time</>
                          )}
                          {subscription?.recurring_interval === "annual" &&
                            subscription?.promo_duration === 2 && (
                              <>/two years</>
                            )}
                          {subscription?.recurring_interval === "annual" &&
                            subscription?.promo_duration !== 2 &&
                            !subscription?.promotion_details?.lifetime && (
                              <>/year</>
                            )}
                          {subscription?.recurring_interval === "month" && (
                            <>/month</>
                          )}
                        </span>
                      </>
                    )}

                    {subscription &&
                      subscription?.subscription_end_date !== "None" &&
                      subscription?.subscription_start_date !== "2024-11-26" &&
                      subscription?.zoee_subscription_id !== "1005003" &&
                      !lifeTimeDuration &&
                      !foundingCoachPromo && (
                        <Button
                          onClick={navigateToSubPlans}
                          variant="blurpleGradient"
                          className="flex w-[158px] min-w-fit items-center gap-[10px] mt-4"
                        >
                          Resubscribe
                        </Button>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {getCurrentPlanSection()}
      </div>
    </div>
  )
}

export default CurrentSubscription
